import BaseChart from './BaseChart';

/**
 * Heatmap chart implementation using VChart
 */
class HeatmapChart extends BaseChart {
  /**
   * Get chart specification for heatmap chart
   * @returns {Object} Chart specification
   */
  getChartSpecification() {
    const commonConfig = this.getCommonConfig();
    const data = this.prepareData();
    
    // Heatmap specific fields — auto-detect from data when defaults don't match
    const xField = this._resolveField('xField', 'x', ['row', 'category', 'period', 'label'])
      || this._resolveNameField();
    let yField = this._resolveField('yField', 'y', ['column', 'group', 'series', 'metric']);
    if (!yField && Array.isArray(this.data) && this.data.length > 0) {
      // Pick the second string field (the one after xField) for the y-axis
      const sample = this.data[0];
      for (const key of Object.keys(sample)) {
        if (key === xField || key.startsWith('_')) continue;
        if (typeof sample[key] === 'string') { yField = key; break; }
      }
    }
    yField = yField || 'y';
    const valueField = this._resolveField('valueField', 'value', ['weight', 'intensity', 'count', 'score'])
      || this._resolveValueField();
    
    return {
      type: 'heatmap',
      data,
      xField,
      yField,
      valueField,
      
      // Color configuration
      color: {
        type: 'linear',
        domain: this.getValueDomain(),
        range: this.options.colorRange || ['#eff3ff', '#2171b5'],
        ...this.options.colorScale
      },
      
      // Heatmap specific styling
      heatmap: {
        style: {
          stroke: '#fff',
          lineWidth: 1,
          ...this.options.heatmapStyle
        },
        state: {
          hover: {
            stroke: '#fff',
            lineWidth: 2
          },
          ...this.options.heatmapState
        }
      },
      
      // Label configuration
      label: {
        visible: this.options.showLabels === true,
        style: {
          fontSize: 10,
          fontWeight: 'normal',
          ...this.options.labelStyle
        },
        formatter: this.options.labelFormatter || ((datum) => {
          return datum[valueField];
        })
      },
      
      // Axes configuration
      axes: [
        {
          orient: 'bottom',
          type: 'band',
          ...this.options.xAxis
        },
        {
          orient: 'left',
          type: 'band',
          ...this.options.yAxis
        }
      ],
      
      // Animation configuration
      animationAppear: {
        duration: 800,
        easing: 'cubicOut',
        ...this.options.animationAppear
      },
      
      // Common configuration from BaseChart
      ...commonConfig
    };
  }
  
  /**
   * Get value domain for color scaling
   * @returns {Array} Value domain [min, max]
   */
  getValueDomain() {
    if (!this.data || this.data.length === 0) {
      return [0, 1];
    }

    const valueField = this._resolveField('valueField', 'value', ['weight', 'intensity', 'count', 'score'])
      || this._resolveValueField();
    let min = Infinity;
    let max = -Infinity;
    
    this.data.forEach(item => {
      if (item[valueField] < min) min = item[valueField];
      if (item[valueField] > max) max = item[valueField];
    });
    
    return [min, max];
  }
}

export default HeatmapChart; 